import { useEffect, useRef } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { ArticlePage } from "./pages/ArticlePage";
import { HomePage } from "./pages/HomePage";
import { JournalIndexPage } from "./pages/JournalIndexPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { ResearchPage } from "./pages/ResearchPage";
import { messagesForLocale, useI18n, type Locale } from "./i18n";
import { setPageMetadata } from "./lib/metadata";

export function metadataForPath(pathname: string, locale: Locale = "en"): {
  title: string;
  description: string;
} {
  const metadata = messagesForLocale(locale).metadata;
  const normalizedPathname =
    pathname.toLowerCase().replace(/\/+$/, "") || "/";

  if (normalizedPathname === "/") {
    return metadata.home;
  }

  if (normalizedPathname === "/journal") {
    return metadata.journal;
  }

  if (normalizedPathname.startsWith("/journal/")) {
    return metadata.article;
  }

  if (normalizedPathname === "/research") {
    return metadata.research;
  }

  if (normalizedPathname === "/privacy") {
    return metadata.privacy;
  }

  return metadata.notFound;
}

export function ScrollManager() {
  const location = useLocation();
  const { locale } = useI18n();
  const hasCommittedRoute = useRef(false);

  useEffect(() => {
    const metadata = metadataForPath(location.pathname, locale);
    setPageMetadata(metadata.title, metadata.description);
  }, [locale, location.pathname]);

  useEffect(() => {
    const hash = location.hash.slice(1);
    const shouldMoveFocus = hasCommittedRoute.current;
    let frame = 0;

    if (hash) {
      frame = requestAnimationFrame(() => {
        const target = document.getElementById(hash);
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        target?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth" });
        if (shouldMoveFocus) {
          const focusTarget =
            target?.querySelector<HTMLElement>("h1, h2, h3") ?? target ?? undefined;
          focusTarget?.setAttribute("tabindex", "-1");
          focusTarget?.setAttribute("data-route-focus", "true");
          focusTarget?.focus({ preventScroll: true });
        }
        hasCommittedRoute.current = true;
      });
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
      frame = requestAnimationFrame(() => {
        if (shouldMoveFocus) {
          const heading = document.querySelector<HTMLElement>("main h1");
          heading?.setAttribute("tabindex", "-1");
          heading?.setAttribute("data-route-focus", "true");
          heading?.focus({ preventScroll: true });
        }
        hasCommittedRoute.current = true;
      });
    }

    return () => cancelAnimationFrame(frame);
  }, [location.pathname, location.search, location.hash, location.key]);

  return null;
}

export function App() {
  return (
    <>
      <ScrollManager />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/journal" element={<JournalIndexPage />} />
        <Route path="/journal/:slug" element={<ArticlePage />} />
        <Route path="/research" element={<ResearchPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}
